import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabase';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);
const LS_KEY = 'ulkd_cart';

const loadLocal = () => { try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; } catch { return []; } };

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(loadLocal);
  const [cartOpen, setCartOpen] = useState(false);
  const prevUserIdRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(items));
  }, [items]);

  // 로그아웃 전환 감지 → 로컬 장바구니 초기화
  useEffect(() => {
    const prevId = prevUserIdRef.current;
    const nextId = user?.id ?? null;
    if (prevId !== null && nextId === null) {
      setItems([]);
      localStorage.removeItem(LS_KEY);
    }
    prevUserIdRef.current = nextId;
  }, [user]);

  const saveToDb = useCallback(async (userId, cartItems) => {
    if (!userId) return;
    if (cartItems.length === 0) {
      await supabase.from('cart_items').delete().eq('user_id', userId);
      return;
    }
    await supabase.from('cart_items').upsert(
      cartItems.map(item => ({
        user_id: userId,
        item_key: item.key,
        product_id: item.productId,
        product_name: item.productName,
        price: item.price,
        img_url: item.img,
        size: item.size,
        color_name: item.colorName,
        color_hex: item.colorHex,
        quantity: item.quantity,
        shipping_fee: item.shippingFee ?? 0,
      }))
    );
  }, []);

  // 로그인 시 DB 장바구니 로드
  useEffect(() => {
    if (!user) return;
    const sync = async () => {
      const { data } = await supabase.from('cart_items').select('*').eq('user_id', user.id);
      if (data?.length > 0) {
        // DB 항목으로 교체 (다른 기기·이전 세션 복원)
        setItems(data.map(d => ({
          key: d.item_key, productId: d.product_id, productName: d.product_name,
          price: d.price, img: d.img_url, size: d.size,
          colorName: d.color_name, colorHex: d.color_hex, quantity: d.quantity,
          shippingFee: d.shipping_fee ?? 0,
        })));
      } else {
        // DB에 없으면 로컬도 비움 (다른 유저의 잔여 항목 방지)
        setItems([]);
        localStorage.removeItem(LS_KEY);
      }
    };
    sync();
  }, [user]);

  const addItem = useCallback((item) => {
    setItems(prev => {
      const existing = prev.find(i => i.key === item.key);
      const next = existing
        ? prev.map(i => i.key === item.key ? { ...i, quantity: i.quantity + 1 } : i)
        : [...prev, { ...item, quantity: 1 }];
      if (user) saveToDb(user.id, next);
      return next;
    });
    setCartOpen(true);
  }, [user, saveToDb]);

  const removeItem = useCallback((key) => {
    setItems(prev => {
      const next = prev.filter(i => i.key !== key);
      if (user) saveToDb(user.id, next);
      return next;
    });
    if (user) supabase.from('cart_items').delete().eq('user_id', user.id).eq('item_key', key);
  }, [user, saveToDb]);

  const updateQty = useCallback((key, qty) => {
    if (qty < 1) { removeItem(key); return; }
    setItems(prev => {
      const next = prev.map(i => i.key === key ? { ...i, quantity: qty } : i);
      if (user) saveToDb(user.id, next);
      return next;
    });
  }, [user, removeItem, saveToDb]);

  const clearCart = useCallback(async () => {
    setItems([]);
    localStorage.removeItem(LS_KEY);
    if (user) await supabase.from('cart_items').delete().eq('user_id', user.id);
  }, [user]);

  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const count = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clearCart, total, count, cartOpen, setCartOpen }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
